<template>
  <div class="terms-container">
    <div class="terms-content">
      <h1>Terms and Conditions</h1>
      <p class="last-updated">Last Updated: August 14, 2026</p>

      <section>
        <h2>1. Introduction</h2>
        <p>
          Welcome to the EAE (Extracurricular Activity Excellence) Platform ("Platform", "we", "us", or "our"),
          operated by Extracurricular Activity Excellence International Pte. Ltd. These Terms and Conditions
          ("Platform Terms") govern your access to and use of the EAE Platform and its associated services.
        </p>
        <p>
          These Platform Terms supplement the EAE International
          <a href="https://www.eae.international/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
          By accessing or using our Platform, you agree to be bound by both. If product, registration, order, or checkout
          terms conflict with the general terms, the more specific terms control for that product or transaction.
        </p>
      </section>

      <section>
        <h2>2. Platform Overview</h2>
        <p>
          EAE is a student-centric portfolio and activity management platform serving international
          high school students. The Platform provides the following services:
        </p>
        <ul>
          <li>Registration for educational competitions and extracurricular activities (including but not limited to WAO, BPA, IFO, Envirothon, IPsyO, IEnvO, IBO, WEC, NIM, and EAE programs)</li>
          <li>Activity portfolio creation and management</li>
          <li>Review entry creation and documentation</li>
          <li>Educational program and pass applications</li>
          <li>Achievement tracking, scoring, and awards</li>
          <li>Educational courses and learning resources</li>
          <li>Team-based activities and collaborations</li>
          <li>Certificate generation and verification</li>
        </ul>
      </section>

      <section>
        <h2>3. User Accounts</h2>
        <h3>3.1 Account Types</h3>
        <p>The Platform supports the following account types:</p>
        <ul>
          <li><strong>Student Accounts:</strong> For individual students participating in activities and competitions</li>
          <li><strong>Advisor Accounts:</strong> For teachers, counselors, or mentors who supervise and recommend students</li>
          <li><strong>School Admin Accounts:</strong> For institutional administrators managing student registrations and programs</li>
        </ul>

        <h3>3.2 Account Registration</h3>
        <p>
          To use certain features of the Platform, you must register for an account. You agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain and promptly update your account information</li>
          <li>Maintain the security and confidentiality of your login credentials</li>
          <li>Accept responsibility for all activities that occur under your account</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
        </ul>

        <h3>3.3 Account Security</h3>
        <p>
          You may access your account via One-Time Password (OTP) sent to your registered email or
          through a password you have set. You are responsible for maintaining the confidentiality
          of your authentication credentials.
        </p>
      </section>

      <section>
        <h2>4. Privacy and Data Protection</h2>
        <p>
          Our collection, use, storage, sharing, and protection of personal information is described in our
          <router-link to="/privacy-policy">Privacy Policy</router-link>, which forms part of the policies
          referenced by these Terms. By using the Platform or submitting information through EAE forms, you
          acknowledge that your information will be handled as described in the Privacy Policy.
        </p>
      </section>

      <section>
        <h2>5. Payment Terms</h2>
        <h3>5.1 Fees and Pricing</h3>
        <p>
          The Platform offers various paid services including Universal Pass subscriptions,
          REPass (individual program passes), program registrations, and official courses.
          All prices are displayed in the applicable currency (USD or SGD) at the time of purchase.
        </p>

        <h3>5.2 Fee Reduction Policy</h3>
        <p>
          We offer a tiered fee reduction system based on country/region economic classification
          and student's school tuition tier. Students may be required to provide proof of school
          tuition to qualify for reduced fees.
        </p>

        <h3>5.3 Payment Processing</h3>
        <p>
          Payments are processed securely through Stripe. A payment service fee may be applied
          on top of product pricing, charged by the payment processor. By making a payment,
          you agree to Stripe's terms of service.
        </p>

        <h3>5.4 Refund Policy</h3>
        <p>
          To request a refund, please send an email to
          <a href="mailto:contact@eae.international">contact@eae.international</a> with the subject line:
          "EAE [Product Type] Refund Request | [Your Name]". Refunds are calculated by:
        </p>
        <ul>
          <li>Deducting project/service costs already incurred</li>
          <li>Calculating monthly pass costs for the usage period</li>
          <li>Applying any incurred usage costs</li>
          <li>Deducting payment service fees (non-refundable)</li>
          <li>Returning the remaining balance</li>
        </ul>
      </section>

      <section>
        <h2>6. User Conduct</h2>
        <p>When using our Platform, you agree NOT to:</p>
        <ul>
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe upon the rights of others, including intellectual property rights</li>
          <li>Submit false, misleading, or fraudulent information</li>
          <li>Impersonate any person or entity</li>
          <li>Interfere with or disrupt the Platform or its servers</li>
          <li>Attempt to gain unauthorized access to any part of the Platform</li>
          <li>Use the Platform for any unlawful or unauthorized purpose</li>
          <li>Share your account credentials with others</li>
          <li>Submit content that is illegal, harmful, threatening, abusive, or otherwise objectionable</li>
          <li>Engage in academic dishonesty, cheating, or plagiarism in any competition or course</li>
        </ul>
      </section>

      <section>
        <h2>7. Intellectual Property</h2>
        <h3>7.1 Platform Content</h3>
        <p>
          All content on the Platform, including but not limited to text, graphics, logos, images,
          audio, video, software, and course materials, is owned by or licensed to EAE and is
          protected by intellectual property laws. You may not reproduce, distribute, modify,
          or create derivative works from this content without our express written permission.
        </p>

        <h3>7.2 User Content</h3>
        <p>
          You retain ownership of content you submit to the Platform (such as activity descriptions,
          review entries, and attachments). By submitting content, you grant us a non-exclusive,
          worldwide, royalty-free license to use, display, and process such content for the purpose
          of providing our services.
        </p>
      </section>

      <section>
        <h2>8. Third-Party Services</h2>
        <p>
          Our Platform integrates with third-party services including Stripe for payments and
          cloud storage providers for file uploads. Your use of these services is subject to
          their respective terms and privacy policies. We are not responsible for the practices
          of third-party services.
        </p>
      </section>

      <section>
        <h2>9. Disclaimers</h2>
        <h3>9.1 Service Availability</h3>
        <p>
          The Platform is provided on an "as is" and "as available" basis. We do not guarantee
          that the Platform will be uninterrupted, secure, or error-free. We reserve the right
          to modify, suspend, or discontinue any aspect of the Platform at any time.
        </p>

        <h3>9.2 Competition and Program Results</h3>
        <p>
          While we strive to provide accurate information about competitions and programs,
          we do not guarantee specific outcomes, awards, or results. Competition results are
          determined by the respective organizing bodies.
        </p>

        <h3>9.3 Educational Content</h3>
        <p>
          Educational content and courses provided on the Platform are for informational and
          learning purposes. We do not guarantee that such content will achieve specific
          educational outcomes or certifications.
        </p>
      </section>

      <section>
        <h2>10. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, EAE and its officers, directors,
          employees, and agents shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages, including but not limited to loss of profits,
          data, or goodwill, arising out of or related to your use of the Platform.
        </p>
        <p>
          Our total liability for any claims arising from your use of the Platform shall not
          exceed the amount you paid to us in the twelve (12) months preceding the claim.
        </p>
      </section>

      <section>
        <h2>11. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless EAE and its officers, directors,
          employees, and agents from and against any claims, liabilities, damages, losses,
          and expenses (including reasonable legal fees) arising out of or related to your
          use of the Platform, your violation of these Terms, or your violation of any rights
          of another party.
        </p>
      </section>

      <section>
        <h2>12. Modifications to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify users of
          material changes by posting the updated Terms on the Platform with a new "Last Updated"
          date. Your continued use of the Platform after such changes constitutes your acceptance
          of the modified Terms.
        </p>
      </section>

      <section>
        <h2>13. Termination</h2>
        <p>
          We may terminate or suspend your account and access to the Platform at our sole
          discretion, without prior notice, for conduct that we believe violates these Terms
          or is harmful to other users, us, or third parties, or for any other reason.
        </p>
        <p>
          Upon termination, your right to use the Platform will immediately cease.
          Provisions that by their nature should survive termination shall survive,
          including but not limited to ownership provisions, warranty disclaimers,
          indemnity, and limitations of liability.
        </p>
      </section>

      <section>
        <h2>14. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of
          the Republic of Singapore, without regard to its conflict of law provisions.
        </p>
        <p>
          Any dispute arising out of or relating to these Terms or your use of the Platform
          shall be subject to the exclusive jurisdiction of the courts of Singapore.
        </p>
        <p>
          Before initiating any legal proceedings, you agree to attempt to resolve any
          dispute informally by contacting us at
          <a href="mailto:contact@eae.international">contact@eae.international</a>.
        </p>
      </section>

      <section>
        <h2>15. Severability</h2>
        <p>
          If any provision of these Terms is found to be unenforceable or invalid, that
          provision shall be limited or eliminated to the minimum extent necessary, and
          the remaining provisions shall remain in full force and effect.
        </p>
      </section>

      <section>
        <h2>16. Entire Agreement</h2>
        <p>
          These Platform Terms, the EAE International
          <a href="https://www.eae.international/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>,
          and any other policies or transaction-specific terms referenced herein constitute the
          entire agreement between you and EAE regarding the use of the Platform and
          supersede any prior agreements or understandings.
        </p>
      </section>

      <section>
        <h2>17. Contact Information</h2>
        <p>
          If you have any questions about these Terms or our Platform, please contact us at:
        </p>
        <p>
          <strong>Email:</strong> <a href="mailto:contact@eae.international">contact@eae.international</a><br>
          <strong>Website:</strong> eae.international
        </p>
      </section>

      <div class="acknowledgment">
        <p>
          By using the EAE Platform, you acknowledge that you have read, understood, and
          agree to be bound by these Terms and Conditions.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TermsAndConditions",
  mounted() {
    document.title = "Terms and Conditions - EAE";
  }
};
</script>

<style scoped lang="scss">
.terms-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 40px 20px;
}

.terms-content {
  max-width: 900px;
  margin: 0 auto;
  background: #ffffff;
  padding: 50px 60px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.8;
  color: #333;

  h1 {
    text-align: center;
    color: #2c3e50;
    margin-bottom: 10px;
    font-size: 32px;
  }

  .last-updated {
    text-align: center;
    color: #666;
    font-size: 14px;
    margin-bottom: 40px;
  }

  h2 {
    color: #2c3e50;
    font-size: 20px;
    margin-top: 35px;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e74c3c;
  }

  h3 {
    color: #34495e;
    font-size: 16px;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 15px;
    text-align: justify;
  }

  ul {
    margin: 15px 0;
    padding-left: 25px;

    li {
      margin-bottom: 10px;
    }
  }

  a {
    color: #e74c3c;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  section {
    margin-bottom: 25px;
  }

  .acknowledgment {
    margin-top: 40px;
    padding: 20px;
    background: #fff8f7;
    border-left: 4px solid #e74c3c;
    border-radius: 4px;

    p {
      margin: 0;
      font-weight: 500;
    }
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .terms-container {
    padding: 20px 10px;
  }

  .terms-content {
    padding: 30px 25px;

    h1 {
      font-size: 24px;
    }

    h2 {
      font-size: 18px;
    }

    h3 {
      font-size: 15px;
    }
  }
}
</style>
